import { fetchMyWorkspaces, getWorkspaceFromAccessToken, startLogin } from '@nexus/supabase';
import { TierKey } from '../sections/upgrade/plan-data';
import { getSubscriptionManagerUrl } from '../config/global';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from './use-auth';
import { pinWorkspaceSwitch } from '../lib/workspace-switch-identity';

export const USER_WORKSPACES_QUERY_KEY = ['user_workspaces'] as const;
const FIVE_MINS_IN_MS = 5 * 60 * 1000;
const TEN_MINS_IN_MS = 10 * 60 * 1000;

export type Workspace = {
  id: string;
  shortName: string;
  subscriptionPlan: TierKey;
  isBillingOwner: boolean;
  federatedLoginDisabled: boolean;
};

export { getWorkspaceFromAccessToken };

export async function getUserWorkspaces(): Promise<Workspace[]> {
  return fetchMyWorkspaces(getSubscriptionManagerUrl()) as Promise<Workspace[]>;
}

export function useWorkspaces() {
  const { data, error, isPending, refetch } = useQuery({
    queryKey: USER_WORKSPACES_QUERY_KEY,
    queryFn: getUserWorkspaces,
    staleTime: FIVE_MINS_IN_MS,
    gcTime: TEN_MINS_IN_MS,
  });

  const userWorkspaces = useMemo(
    () => (data ? data.filter((workspace) => !workspace.federatedLoginDisabled) : []),
    [data]
  );
  const { currentWorkspaceId } = useCurrentWorkspace();
  const crtWorkspaceData = useMemo(() => {
    if (currentWorkspaceId === null || !data) {
      return null;
    }
    // Unfiltered lookup: the workspace the token pins us to may itself have
    // federatedLoginDisabled, and still needs its own billing/plan data.
    return data.find((w) => w.id === currentWorkspaceId) ?? null;
  }, [data, currentWorkspaceId]);

  return {
    userWorkspaces,
    crtWorkspaceData,
    error,
    refetch,
    isLoading: isPending,
  };
}

export function useCurrentWorkspace() {
  const { session, user } = useAuth();
  const currentWorkspaceId = getWorkspaceFromAccessToken(session?.access_token);
  const [pendingWorkspace, setPendingWorkspace] = useState<Workspace | null>(null);
  const [timeoutDialogOpen, setTimeoutDialogOpen] = useState<boolean>(false);
  const [switchError, setSwitchError] = useState<string | null>(null);

  useEffect(() => {
    // if the new access token was already processed
    if (pendingWorkspace !== null && currentWorkspaceId === pendingWorkspace.id) {
      setPendingWorkspace(null);
    }
  }, [pendingWorkspace, currentWorkspaceId]);

  const switchWorkspace = useCallback(
    async (workspace: Workspace) => {
      setPendingWorkspace(workspace);
      // CLOUD-728: maybe someone just disabled federated login so switching is not possible
      const errorCode = await validateWorkspaceSwitch(workspace.id);
      if (errorCode !== null) {
        setPendingWorkspace(null);
        setSwitchError(errorCode);
        return;
      }
      // CLOUD-715: the BC IDP answers this flow from its own session, which can
      // belong to another account. useWorkspaceSwitchGuard checks the pin on return.
      pinWorkspaceSwitch(user?.email, workspace.id);
      // this will trigger a fresh login flow for the new workspace without any user interaction (only redirects)
      // in the end, ari cloud will load from scratch with the new identity
      startLogin('bc-idp', window.location.origin, workspace.id);
      setTimeout(() => {
        setPendingWorkspace(null);
        setTimeoutDialogOpen(true);
      }, 15000);
    },
    [user?.email]
  );

  const closeTimeoutDialog = useCallback(() => {
    setTimeoutDialogOpen(false);
  }, [setTimeoutDialogOpen]);

  const closeSwitchErrorDialog = useCallback(() => {
    setSwitchError(null);
  }, [setSwitchError]);

  return {
    currentWorkspaceId,
    pendingWorkspace,
    switchWorkspace,
    timeoutDialogOpen,
    closeTimeoutDialog,
    switchError,
    closeSwitchErrorDialog,
  };
}

async function validateWorkspaceSwitch(targetWorkspaceId: string): Promise<string | null> {
  try {
    const latestWorkspaces = await getUserWorkspaces();
    if (!latestWorkspaces) {
      return 'unexpectedError';
    }
    if (!latestWorkspaces.some((ws) => ws.id === targetWorkspaceId)) {
      return 'workspaceRemoved';
    }
    if (latestWorkspaces.some((ws) => ws.id === targetWorkspaceId && ws.federatedLoginDisabled)) {
      return 'federatedLoginDisabled';
    }
    return null;
  } catch {
    return 'unexpectedError';
  }
}
